<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDebounceFn } from '@vueuse/core';
import ConversationApi from 'dashboard/api/inbox/conversation';
import { useAlert } from 'dashboard/composables';
import { useKanbanStagesStore } from 'dashboard/stores/kanbanStages';
import wootConstants from 'dashboard/constants/globals';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import SelectMenu from 'dashboard/components-next/selectmenu/SelectMenu.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const { t } = useI18n();
const kanbanStagesStore = useKanbanStagesStore();

const dialogRef = ref(null);
const stageId = ref(null);
const query = ref('');
const status = ref(wootConstants.STATUS_TYPE.OPEN);
const page = ref(1);
const results = ref([]);
const selectedIds = ref(new Set());
const isSearching = ref(false);
const isLoadingMore = ref(false);
const isAssigning = ref(false);
const hasSearched = ref(false);
const hasMore = ref(false);

const statusOptions = computed(() => [
  { label: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.open.TEXT'), value: 'open' },
  {
    label: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.pending.TEXT'),
    value: 'pending',
  },
  {
    label: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.resolved.TEXT'),
    value: 'resolved',
  },
  {
    label: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.snoozed.TEXT'),
    value: 'snoozed',
  },
  { label: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.all.TEXT'), value: 'all' },
]);

const isSearchMode = computed(() => !!query.value.trim());
const selectedCount = computed(() => selectedIds.value.size);

const resetState = () => {
  query.value = '';
  status.value = wootConstants.STATUS_TYPE.OPEN;
  page.value = 1;
  results.value = [];
  selectedIds.value = new Set();
  hasSearched.value = false;
  hasMore.value = false;
};

const closeDialog = () => dialogRef.value?.close();

const PAGE_SIZE = 25;

const runSearch = async ({ reset = false } = {}) => {
  const isLoadingFirstPage = reset || page.value === 1;
  if (isLoadingFirstPage) {
    isSearching.value = true;
  } else {
    isLoadingMore.value = true;
  }

  try {
    const { data } = await ConversationApi.search({
      q: isSearchMode.value ? query.value.trim() : undefined,
      status: isSearchMode.value ? undefined : status.value,
      page: reset ? 1 : page.value,
    });
    const payload = data.payload || [];
    results.value = reset ? payload : [...results.value, ...payload];
    hasMore.value = payload.length >= PAGE_SIZE;
    page.value = reset ? 2 : page.value + 1;
  } catch (error) {
    if (reset) results.value = [];
  } finally {
    isSearching.value = false;
    isLoadingMore.value = false;
    hasSearched.value = true;
  }
};

const open = stage => {
  stageId.value = stage.id;
  resetState();
  dialogRef.value?.open();
  runSearch({ reset: true });
};

const handleSearch = useDebounceFn(() => runSearch({ reset: true }), 300);

const handleQueryInput = value => {
  query.value = value;
  handleSearch();
};

const handleStatusChange = value => {
  status.value = value;
  runSearch({ reset: true });
};

const toggleSelection = conversationId => {
  const next = new Set(selectedIds.value);
  if (next.has(conversationId)) {
    next.delete(conversationId);
  } else {
    next.add(conversationId);
  }
  selectedIds.value = next;
};

const allVisibleSelected = computed(
  () =>
    results.value.length > 0 &&
    results.value.every(c => selectedIds.value.has(c.id))
);

const toggleSelectAllVisible = () => {
  if (allVisibleSelected.value) {
    selectedIds.value = new Set();
  } else {
    selectedIds.value = new Set(results.value.map(c => c.id));
  }
};

const handleAddSelected = async () => {
  const conversations = results.value.filter(c => selectedIds.value.has(c.id));
  if (!conversations.length) return;

  isAssigning.value = true;
  try {
    await kanbanStagesStore.assignConversationsToStage({
      conversations,
      stageId: stageId.value,
    });
    useAlert(
      t('KANBAN.BOARD.ADD_CONVERSATION.SUCCESS_MESSAGE', {
        count: conversations.length,
      })
    );
    closeDialog();
  } catch (error) {
    useAlert(t('KANBAN.BOARD.ADD_CONVERSATION.ERROR_MESSAGE'));
  } finally {
    isAssigning.value = false;
  }
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('KANBAN.BOARD.ADD_CONVERSATION.TITLE')"
    width="md"
    :show-confirm-button="false"
    :cancel-button-label="t('DIALOG.BUTTONS.CLOSE')"
    @close="resetState"
  >
    <div class="flex flex-col gap-3">
      <div class="flex items-center gap-2">
        <Input
          :model-value="query"
          :placeholder="t('KANBAN.BOARD.ADD_CONVERSATION.SEARCH_PLACEHOLDER')"
          autofocus
          class="grow"
          @update:model-value="handleQueryInput"
        />
        <div :class="{ 'opacity-50 pointer-events-none': isSearchMode }">
          <SelectMenu
            :options="statusOptions"
            :model-value="status"
            :label="
              statusOptions.find(o => o.value === status)?.label || status
            "
            @update:model-value="handleStatusChange"
          />
        </div>
      </div>
      <p v-if="isSearchMode" class="text-xs text-n-slate-11">
        {{ t('KANBAN.BOARD.ADD_CONVERSATION.SEARCH_IGNORES_STATUS') }}
      </p>

      <div
        v-if="results.length"
        class="flex items-center gap-2 px-1 text-xs text-n-slate-11"
      >
        <Checkbox
          :model-value="allVisibleSelected"
          @update:model-value="toggleSelectAllVisible"
        />
        <span>{{ t('KANBAN.BOARD.ADD_CONVERSATION.SELECT_ALL') }}</span>
      </div>

      <div class="flex flex-col gap-1 max-h-72 overflow-y-auto">
        <div v-if="isSearching" class="flex justify-center p-4">
          <Spinner />
        </div>
        <template v-else>
          <label
            v-for="conversation in results"
            :key="conversation.id"
            class="flex items-center gap-2 p-2 text-left rounded-lg cursor-pointer hover:bg-n-slate-3"
          >
            <Checkbox
              :model-value="selectedIds.has(conversation.id)"
              @update:model-value="toggleSelection(conversation.id)"
            />
            <span class="flex flex-col min-w-0 grow">
              <span class="text-sm font-medium truncate text-n-slate-12">
                {{ conversation.contact?.name || conversation.contact?.id }}
              </span>
              <span class="text-xs truncate text-n-slate-11">
                {{
                  t('KANBAN.BOARD.ADD_CONVERSATION.CONVERSATION_ID_LABEL', {
                    id: conversation.id,
                  })
                }}
                <template v-if="conversation.inbox?.name">
                  · {{ conversation.inbox.name }}
                </template>
              </span>
            </span>
          </label>
        </template>
        <p
          v-if="!isSearching && hasSearched && !results.length"
          class="p-2 text-sm text-n-slate-11"
        >
          {{ t('KANBAN.BOARD.ADD_CONVERSATION.NO_RESULTS') }}
        </p>
        <Button
          v-if="hasMore && !isSearching"
          :label="t('KANBAN.BOARD.ADD_CONVERSATION.LOAD_MORE')"
          variant="link"
          size="sm"
          :is-loading="isLoadingMore"
          class="self-center"
          @click="runSearch()"
        />
      </div>
    </div>

    <template #footer>
      <div class="flex items-center justify-between w-full gap-3">
        <Button
          :label="t('DIALOG.BUTTONS.CANCEL')"
          variant="link"
          type="reset"
          class="h-10 hover:!no-underline hover:text-n-brand"
          @click="closeDialog"
        />
        <Button
          :label="
            t('KANBAN.BOARD.ADD_CONVERSATION.ADD_SELECTED', {
              count: selectedCount,
            })
          "
          color="blue"
          type="submit"
          :disabled="!selectedCount || isAssigning"
          :is-loading="isAssigning"
          @click="handleAddSelected"
        />
      </div>
    </template>
  </Dialog>
</template>
